import { request } from './client'

/* ============================================================================
   PAIRS, AND THE FLOORS THEY WORK.

   A team has NO NAME — the client's own answer on 16 September, and a better one than a
   name: a named team needs a screen to name it, a rule for what the name means when a
   member leaves, and somebody to notice when "Team B" has had neither of its original
   men for a year. Two names read off the roster cannot go stale.

   So every type below carries BOTH members' names, and the screen joins them. There is
   deliberately no `name` field to fall back on.
   ========================================================================= */

export interface TeamFloor {
  floorId: string
  floorCode: string
  buildingName: string
  /** Null falls back to the Latin name rather than drawing a blank — and its ABSENCE was
   *  a defect: without it a pair's row read "North building" in the middle of an Arabic
   *  screen (#128). */
  buildingNameAr: string | null
}

export interface Team {
  teamId: string
  memberOneUserId: string
  memberOneName: string
  memberOneNameAr: string | null
  memberTwoUserId: string
  memberTwoName: string
  memberTwoNameAr: string | null
  /** Who is covering for whom today. Null on an ordinary day. */
  absentUserId: string | null
  standInUserId: string | null
  standInName: string | null
  standInNameAr: string | null
  floors: TeamFloor[]
  /** Open rooms across those floors — what the pair is actually carrying. */
  openRooms: number
  openCredits: number
}

export interface FloorTeam {
  floorId: string
  floorCode: string
  buildingId: string
  buildingName: string
  buildingNameAr: string | null
  rooms: number
  /** Null when nobody has been given this floor today. */
  teamId: string | null
}

export interface TeamCandidate {
  userId: string
  employeeId: string
  name: string
  nameAr: string | null
  /** False for a man on leave or a rest day: drawn, and not choosable. An attendant who
   *  vanished from the list would leave a supervisor wondering where he had gone. */
  onShift: boolean
}

export interface TeamsView {
  propertyId: string
  localDate: string
  teams: Team[]
  floors: FloorTeam[]
  /** Attendants on no pair — both the list a team is made from and the answer to
   *  "why is floor 4 short today". */
  unpaired: TeamCandidate[]
}

export const fetchTeams = (
  propertyId: string,
  options: { date?: string; signal?: AbortSignal } = {},
) => request<TeamsView>(
  `/properties/${propertyId}/teams${options.date ? `?date=${options.date}` : ''}`,
  options.signal ? { signal: options.signal } : {},
)

export const createTeam = (
  propertyId: string, memberOneUserId: string, memberTwoUserId: string,
) => request<Team>(`/properties/${propertyId}/teams`, {
  method: 'POST',
  body: { memberOneUserId, memberTwoUserId },
})

export const disbandTeam = (propertyId: string, teamId: string) =>
  request<void>(`/properties/${propertyId}/teams/${teamId}/disband`, { method: 'POST' })

/** Cover for a man for the day. **Nothing is reassigned** — the pair is two different
 *  men today, and the rooms never move. */
export const substituteMember = (
  propertyId: string, teamId: string,
  absentUserId: string, standInUserId: string, reason?: string,
) => request<Team>(`/properties/${propertyId}/teams/${teamId}/substitute`, {
  method: 'POST',
  body: { absentUserId, standInUserId, ...(reason ? { reason } : {}) },
})

export const clearSubstitution = (propertyId: string, teamId: string) =>
  request<void>(`/properties/${propertyId}/teams/${teamId}/substitute/clear`, { method: 'POST' })

/** Give a floor to a pair, and hand them its unstarted rooms. Resolves with HOW MANY
 *  rooms followed — a supervisor pressing this needs to know whether the board is now
 *  right, which "ok" does not answer. */
export const assignFloorToTeam = (propertyId: string, floorId: string, teamId: string) =>
  request<{ roomsMoved: number }>(
    `/properties/${propertyId}/floors/${floorId}/team`, { method: 'POST', body: { teamId } })

export const clearFloorTeam = (propertyId: string, floorId: string) =>
  request<void>(`/properties/${propertyId}/floors/${floorId}/team/clear`, { method: 'POST' })
